import { useCallback, useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import moment from 'moment/moment'

import Search from 'components/Search'
import Spinner from 'components/Spinner'
import SearchHistory from 'components/SearchHistory'

import useDeviceSearch from 'hooks/useDeviceSearch'
import useFavorites from 'hooks/useFavorites'
import useSearchHistory from 'hooks/useSearchHistory'
import useSearchParams from 'hooks/useSearchParams'
import { useSessionContext } from 'hooks/SessionContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import MultipleDevicesResults from 'pages/DeviceSearch/MultipleDevicesResults'

import URLParamsHelper from 'helpers/url_params_helper'

import Api from 'services/api'

import { SinglePanePage, PageTitle } from 'styles/shared'

const DeviceSearch = () => {
  const SEARCH_HISTORY_TYPE = 'devices'

  const { id: deviceId } = useParams() || ''
  const navigate = useNavigate()
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())
  const [isSearchHistoryShown, setIsSearchHistoryShown] = useState(false)

  const queryParam = useSearchParams('q')
  const queryText = deviceId || queryParam || localStorage.getItem('devicesQuery') || ''

  const { enabledFeatures } = useFeaturesContext()

  const parseDeviceIds = useCallback((deviceIdStr) => {
    if (!deviceIdStr || deviceIdStr.trim().length === 0) { return [] }

    return _.uniq(_.escape(deviceIdStr.replace(/[,\s]+/g, ' ').trim()).split(' ')).sort()
  }, [])

  const [query, setQuery] = useState(parseDeviceIds(queryText).join(', '))
  const [deviceIds, setDeviceIds] = useState(parseDeviceIds(queryText))
  const [showSpinner, setShowSpinner] = useState(false)

  const { session: { userId } } = useSessionContext()
  const { searchHistory, isSearchHistoryLoaded } = useSearchHistory(userId, SEARCH_HISTORY_TYPE, refreshTimestamp)
  const { devices, invalidDeviceIds, areDevicesLoaded } = useDeviceSearch(deviceIds)

  useFavorites(userId)

  if (isSearchHistoryLoaded && !isSearchHistoryShown) {
    setIsSearchHistoryShown(true)
  }

  useEffect(() => {
    if (parseDeviceIds(queryParam) !== parseDeviceIds(queryText)) {
      if (deviceIds?.length === 1) {
        window.history.replaceState({}, '', '/devices/' + deviceIds[0])
      } else if (deviceIds?.length > 1) {
        URLParamsHelper.update('q', parseDeviceIds(queryText).join(','))
      }
    }
  }, [deviceIds, parseDeviceIds, queryParam, queryText])

  const hasQueryChanged = useCallback((newQuery) => {
    const newQueriedDeviceIds = parseDeviceIds(newQuery) || []

    return deviceIds.length !== newQueriedDeviceIds.length || _.difference(newQueriedDeviceIds, deviceIds).length
  }, [deviceIds, parseDeviceIds])

  const updateSearchHistory = useCallback((deviceIds) => {
    Api.SearchHistory.modify(userId, SEARCH_HISTORY_TYPE, deviceIds.join(', '))
      .then(() => setRefreshTimestamp(moment().valueOf()))
  }, [userId])

  const updateSearch = useCallback((newQuery) => {
    setQuery(newQuery)

    const newQueriedDeviceIds = parseDeviceIds(newQuery)

    updateSearchHistory(newQueriedDeviceIds)

    localStorage.setItem('devicesQuery', newQuery)

    setDeviceIds(newQueriedDeviceIds)

    const priorSelectedDeviceIds = parseDeviceIds(localStorage.getItem('devicesSelected'))

    localStorage.setItem('devicesSelected', _.intersection(priorSelectedDeviceIds, newQueriedDeviceIds).join(','))
  }, [parseDeviceIds, updateSearchHistory])

  const onSearch = useCallback((newQuery) => {
    if (!hasQueryChanged(newQuery)) {
      return
    }

    updateSearch(newQuery)

    const newQueriedDeviceIds = parseDeviceIds(newQuery)
    if (newQueriedDeviceIds?.length === 1) {
      if (enabledFeatures.includes('system_dashboard')) {
        navigate(`/devices/${newQueriedDeviceIds[0]}/dashboard`)
      } else {
        navigate(`/devices/${newQueriedDeviceIds[0]}`)
      }
    } else if (newQueriedDeviceIds?.length > 1) {
      navigate('/devices?q=' + newQueriedDeviceIds.join(','))
    } else {
      navigate('/devices')
    }
  }, [navigate, hasQueryChanged, parseDeviceIds, updateSearch])

  useEffect(() => {
    const newQueriedDeviceIds = parseDeviceIds(queryText) || []

    if (deviceIds.length !== newQueriedDeviceIds.length || _.difference(newQueriedDeviceIds, deviceIds).length) {
      // this condition occurs when navigating with the browser back and forward button
      updateSearch(newQueriedDeviceIds.join(', ')) // WIP: spec
    } else if (searchHistory[0]?.query !== queryText) { // query does not match recent search history
      // this condition can occur on itial load or when selecting an item from search history
      updateSearchHistory(newQueriedDeviceIds)
    }
  }, [queryText, updateSearch, deviceIds, parseDeviceIds, updateSearchHistory]) // eslint-disable-line react-hooks/exhaustive-deps

  const onClickClearIcon = () => {
    setShowSpinner(false)
    setQuery('')
    onSearch('')
  }
  return (
    <S.SinglePanePage>
      <PageTitle>DEVICE SEARCH</PageTitle>

      <Search
        focusOnMount
        ariaLabel="device search"
        name="deviceId"
        placeholder="Device AUID or Serial Number"
        helperText="Search against a single AUID / Serial Number or enter multiple values comma separated."
        value={query}
        onChange={setQuery}
        onSearch={onSearch}
        onClickClearIcon={onClickClearIcon}
        showSpinner={showSpinner}
      />

      { isSearchHistoryShown &&
        <SearchHistory
          onClickCallBack={onSearch}
          type={SEARCH_HISTORY_TYPE}
          query={query}
          searchHistory={searchHistory}
        />
      }

      { !(isSearchHistoryLoaded && areDevicesLoaded) && <Spinner variant="page" /> }

      { isSearchHistoryLoaded && areDevicesLoaded &&
        <MultipleDevicesResults
          pathRoot="devices"
          {...{ devices, invalidDeviceIds }}
        />
      }
    </S.SinglePanePage>
  )
}

export default DeviceSearch

const S = {
  SinglePanePage: styled(SinglePanePage)`
    min-height: calc(100% - 10px);
  `
}
